import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@material-ui/core';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CardGiftcardIcon from '@material-ui/icons/CardGiftcard';
import DescriptionIcon from '@material-ui/icons/Description';
import EditIcon from '@material-ui/icons/Edit';
import RedeemIcon from '@material-ui/icons/Redeem';
import {
  getReferralContent,
  logReferralTabView,
  submitReferral,
} from '../../../actions/referral.actions';

const FORM_FIELDS = [
  {
    name: 'property_name',
    label: 'Property Name',
    required: true,
    placeholder: 'e.g. XYZ Property',
  },
  {
    name: 'contact_full_name',
    label: 'Contact Person Full Name',
    required: true,
    placeholder: 'First and last name',
  },
  {
    name: 'contact_email',
    label: 'Contact Email',
    required: true,
    placeholder: 'name@company.com',
  },
  {
    name: 'contact_phone',
    label: 'Contact Phone',
    required: true,
    placeholder: '(555) 000-0000',
  },
  {
    name: 'contact_role',
    label: 'Contact Role',
    required: true,
    placeholder: 'e.g. Property Manager',
  },
  {
    name: 'notes',
    label: 'Notes',
    required: false,
    placeholder: 'Anything we should know?',
  },
];

const DEFAULT_HERO_CONTENT =
  'Virtual Parking Permits has helped over 600 properties manage their parking efficiently and streamline their parking enforcement with an advanced license plate recognition system';

const DEFAULT_INSTRUCTIONS =
  'Complete the form below to refer a property. Our team will follow up with the contact you provide.';

const DEFAULT_THANK_YOU_MESSAGE =
  'Thank you for your referral! Our team will review your submission and follow up soon.';

const DEFAULT_STEPS = [
  {
    title: 'Fill out the referral form',
    description: 'Tell us about the property and who to contact',
    Icon: EditIcon,
    highlight: false,
  },
  {
    title: 'Your colleagues sign a contract',
    description: 'We reach out and get them set up',
    Icon: DescriptionIcon,
    highlight: false,
  },
  {
    title: 'We send you a $150 gift card',
    description: 'Per property that signs up',
    Icon: RedeemIcon,
    highlight: true,
  },
];

const useStyles = makeStyles((theme) => ({
  page: {
    marginRight: 32,
    paddingBottom: 40,
    display: 'flex',
    flexDirection: 'column',
    gap: 28,
  },
  hero: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 10,
    background: 'linear-gradient(115deg, #e63e52 0%, #ef5d63 45%, #f4837f 100%)',
    padding: '38px 44px 42px',
    color: '#ffffff',
    boxShadow: '0 6px 18px rgba(201,19,79,0.25)',
  },
  heroOrb1: {
    position: 'absolute',
    right: -80,
    top: -110,
    width: 340,
    height: 340,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.10)',
  },
  heroOrb2: {
    position: 'absolute',
    right: 60,
    bottom: -140,
    width: 260,
    height: 260,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.08)',
  },
  heroOrb3: {
    position: 'absolute',
    left: -60,
    bottom: -180,
    width: 300,
    height: 300,
    borderRadius: '50%',
    background: 'rgba(28,54,97,0.10)',
  },
  heroContent: {
    position: 'relative',
    maxWidth: 760,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    alignSelf: 'flex-start',
    background: 'rgba(255,255,255,0.18)',
    border: '1px solid rgba(255,255,255,0.35)',
    borderRadius: 999,
    padding: '5px 14px',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 1.2,
  },
  badgeIcon: {
    fontSize: 15,
  },
  heroTitle: {
    margin: 0,
    fontSize: 30,
    fontWeight: 700,
    lineHeight: 1.2,
    color: '#ffffff',
  },
  heroBody: {
    margin: 0,
    fontSize: 15,
    lineHeight: 1.65,
    color: 'rgba(255,255,255,0.95)',
    whiteSpace: 'pre-wrap',
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.35fr) minmax(300px, 0.85fr)',
    gap: 28,
    alignItems: 'start',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  formCard: {
    background: '#ffffff',
    borderRadius: 10,
    boxShadow: '0 2px 8px rgba(28,54,97,0.08)',
    padding: '36px 40px 40px',
    borderTop: '4px solid #c9134f',
    width: '100%',
    boxSizing: 'border-box',
  },
  sectionTitle: {
    margin: 0,
    fontSize: 19,
    fontWeight: 700,
    color: '#1c3661',
  },
  formTitleRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 32,
  },
  formInstructions: {
    margin: 0,
    fontSize: 14,
    lineHeight: 1.55,
    color: '#5f6b80',
    whiteSpace: 'pre-wrap',
  },
  fieldsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '28px 32px',
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    minWidth: 0,
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: 700,
    color: '#1c3661',
  },
  requiredMark: {
    color: '#c9134f',
  },
  textFieldRoot: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      background: '#fafbfc',
      borderRadius: 6,
      minHeight: 44,
      '& fieldset': {
        borderColor: '#d4d9e0',
      },
      '&:hover fieldset': {
        borderColor: '#d4d9e0',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#c9134f',
        borderWidth: 1,
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: '12px 14px',
      fontSize: 14,
      color: '#202124',
      height: 'auto',
      boxSizing: 'border-box',
    },
  },
  submitRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 36,
  },
  submitButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    background: '#c9134f',
    color: '#ffffff',
    borderRadius: 999,
    padding: '14px 36px',
    fontSize: 13.5,
    fontWeight: 700,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    boxShadow: '0 3px 10px rgba(201,19,79,0.35)',
    '&:hover': {
      background: '#a30d3f',
    },
    '&.Mui-disabled': {
      background: '#c9134f',
      color: '#ffffff',
      opacity: 0.7,
    },
  },
  submitIcon: {
    fontSize: 17,
  },
  stepsColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
  stepsTitle: {
    margin: '4px 0 18px',
    fontSize: 19,
    fontWeight: 700,
    color: '#1c3661',
  },
  stepCard: {
    background: '#ffffff',
    borderRadius: 10,
    boxShadow: '0 2px 8px rgba(28,54,97,0.08)',
    padding: '24px 26px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16,
  },
  stepCardHighlight: {
    background: '#1c3661',
    boxShadow: '0 2px 8px rgba(28,54,97,0.20)',
  },
  stepNumber: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    background: '#fbe9f0',
    color: '#c9134f',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 19,
    fontWeight: 700,
    flexShrink: 0,
  },
  stepNumberHighlight: {
    background: 'rgba(255,255,255,0.14)',
    color: '#ffffff',
  },
  stepText: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    paddingTop: 2,
    flex: 1,
  },
  stepTitle: {
    fontSize: 15.5,
    fontWeight: 700,
    color: '#1c3661',
  },
  stepTitleHighlight: {
    color: '#ffffff',
  },
  stepDescription: {
    fontSize: 13.5,
    lineHeight: 1.55,
    color: '#5f6b80',
  },
  stepDescriptionHighlight: {
    color: 'rgba(255,255,255,0.75)',
  },
  stepIcon: {
    fontSize: 26,
    color: '#1c3661',
    paddingTop: 8,
  },
  stepIconHighlight: {
    color: '#f4837f',
  },
  stepConnector: {
    display: 'flex',
    justifyContent: 'center',
    padding: '6px 0',
  },
  connectorIcon: {
    fontSize: 28,
    color: '#c9134f',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: '24px 0',
  },
}));

const ReferralProgram = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { register, handleSubmit, reset, errors } = useForm();

  const [content, setContent] = useState(DEFAULT_HERO_CONTENT);
  const [badge, setBadge] = useState('EARN A $150 GIFT CARD');
  const [instructions, setInstructions] = useState(DEFAULT_INSTRUCTIONS);
  const [thankYouMessage, setThankYouMessage] = useState(DEFAULT_THANK_YOU_MESSAGE);
  const [steps, setSteps] = useState(DEFAULT_STEPS);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    dispatch(logReferralTabView());
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;

    dispatch(getReferralContent())
      .then((response) => {
        if (cancelled) {
          return;
        }
        const data = response?.data || {};
        setContent(data.content || DEFAULT_HERO_CONTENT);
        setBadge(data.badge || 'EARN A $150 GIFT CARD');
        setInstructions(data.instructions || DEFAULT_INSTRUCTIONS);
        setThankYouMessage(data.thank_you_message || DEFAULT_THANK_YOU_MESSAGE);
        setSteps([
          {
            ...DEFAULT_STEPS[0],
            title: data.step_1_title || DEFAULT_STEPS[0].title,
            description: data.step_1_description || DEFAULT_STEPS[0].description,
          },
          {
            ...DEFAULT_STEPS[1],
            title: data.step_2_title || DEFAULT_STEPS[1].title,
            description: data.step_2_description || DEFAULT_STEPS[1].description,
          },
          {
            ...DEFAULT_STEPS[2],
            title: data.step_3_title || DEFAULT_STEPS[2].title,
            description: data.step_3_description || DEFAULT_STEPS[2].description,
          },
        ]);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setContent(DEFAULT_HERO_CONTENT);
        setInstructions(DEFAULT_INSTRUCTIONS);
        setThankYouMessage(DEFAULT_THANK_YOU_MESSAGE);
        setSteps(DEFAULT_STEPS);
        enqueueSnackbar('Unable to load referral program content.', {
          variant: 'error',
        });
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingContent(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, enqueueSnackbar]);

  const onSubmit = useCallback(
    (formData) => {
      setFieldErrors({});
      setIsSubmitting(true);
      dispatch(submitReferral(formData))
        .then((response) => {
          const data = response?.data || {};
          enqueueSnackbar(thankYouMessage, { variant: 'success' });
          if (data.notification_warning) {
            enqueueSnackbar(data.notification_warning, { variant: 'warning' });
          }
          if (data.confirmation_warning) {
            enqueueSnackbar(data.confirmation_warning, { variant: 'warning' });
          }
          reset({
            property_name: '',
            contact_full_name: '',
            contact_email: '',
            contact_phone: '',
            contact_role: '',
            notes: '',
          });
        })
        .catch(({ response }) => {
          const { data } = response || {};
          setFieldErrors(data || {});
          enqueueSnackbar(
            'Error submitting referral. Please check the form and try again.',
            { variant: 'error' }
          );
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    },
    [dispatch, enqueueSnackbar, reset, thankYouMessage]
  );

  const getHelperText = (name) => {
    if (errors[name]?.message) {
      return errors[name].message;
    }
    if (fieldErrors[name]) {
      return Array.isArray(fieldErrors[name])
        ? fieldErrors[name][0]
        : fieldErrors[name];
    }
    return null;
  };

  return (
    <Box className={classes.page}>
      <Box className={classes.hero}>
        <Box className={classes.heroOrb1} />
        <Box className={classes.heroOrb2} />
        <Box className={classes.heroOrb3} />
        <Box className={classes.heroContent}>
          <Box className={classes.badge}>
            <CardGiftcardIcon className={classes.badgeIcon} />
            <span>{badge}</span>
          </Box>
          <Typography component="h1" className={classes.heroTitle}>
            Referral Program
          </Typography>
          {isLoadingContent ? (
            <Box className={classes.loading}>
              <CircularProgress size={28} style={{ color: '#ffffff' }} />
            </Box>
          ) : (
            <Typography className={classes.heroBody}>{content}</Typography>
          )}
        </Box>
      </Box>

      <Box className={classes.layout}>
        <Box className={classes.formCard} component="section">
          <Box className={classes.formTitleRow}>
            <Typography component="h2" className={classes.sectionTitle}>
              Referral Form
            </Typography>
            {instructions && (
              <Typography
                id="referral_form_instructions"
                className={classes.formInstructions}
              >
                {instructions}
              </Typography>
            )}
          </Box>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box className={classes.fieldsGrid}>
              {FORM_FIELDS.map(({ name, label, required, placeholder }) => (
                <Box className={classes.field} key={name}>
                  <Typography component="label" htmlFor={`referral_${name}`} className={classes.fieldLabel}>
                    {label}
                    {required && (
                      <Box component="span" className={classes.requiredMark}>
                        {' '}
                        *
                      </Box>
                    )}
                  </Typography>
                  <TextField
                    id={`referral_${name}`}
                    className={classes.textFieldRoot}
                    margin="none"
                    name={name}
                    type="text"
                    variant="outlined"
                    placeholder={placeholder}
                    size="small"
                    inputRef={register({
                      required: required ? `${label} is required.` : false,
                      ...(name === 'contact_email' && {
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: 'Email address is not in the right format.',
                        },
                      }),
                    })}
                    error={!!errors[name] || !!fieldErrors[name]}
                    helperText={getHelperText(name)}
                    fullWidth
                  />
                </Box>
              ))}
            </Box>
            <Box className={classes.submitRow}>
              <Button
                id="referral_submit_button"
                type="submit"
                className={classes.submitButton}
                disabled={isSubmitting}
              >
                Submit Form
                <ArrowForwardIcon className={classes.submitIcon} />
              </Button>
            </Box>
          </form>
        </Box>

        <Box className={classes.stepsColumn}>
          <Typography component="h2" className={classes.stepsTitle}>
            How It Works
          </Typography>
          {steps.map((step, index) => {
            const { Icon, highlight, title, description } = step;

            return (
              <React.Fragment key={`referral-step-${index}`}>
                {index > 0 && (
                  <Box className={classes.stepConnector}>
                    <ArrowDownwardIcon className={classes.connectorIcon} />
                  </Box>
                )}
                <Box
                  className={`${classes.stepCard}${
                    highlight ? ` ${classes.stepCardHighlight}` : ''
                  }`}
                >
                  <Box
                    className={`${classes.stepNumber}${
                      highlight ? ` ${classes.stepNumberHighlight}` : ''
                    }`}
                  >
                    {index + 1}
                  </Box>
                  <Box className={classes.stepText}>
                    <Typography
                      className={`${classes.stepTitle}${
                        highlight ? ` ${classes.stepTitleHighlight}` : ''
                      }`}
                    >
                      {title}
                    </Typography>
                    <Typography
                      className={`${classes.stepDescription}${
                        highlight ? ` ${classes.stepDescriptionHighlight}` : ''
                      }`}
                    >
                      {description}
                    </Typography>
                  </Box>
                  <Icon
                    className={`${classes.stepIcon}${
                      highlight ? ` ${classes.stepIconHighlight}` : ''
                    }`}
                  />
                </Box>
              </React.Fragment>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default ReferralProgram;
